<template>
    <div class="d-flex flex-column flex-sm-row flex-wrap" style="gap: 12px">
        <div>
            <v-text-field
                ref="search"
                v-model="searchString"
                label="search"
                :suffix="searchInfo"
                @keydown="$emit('search-keydown', $event)"
                clearable
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                outlined
                class="search"
            />
        </div>

        <v-autocomplete
            v-if="nodes"
            v-model="selectedNodes"
            :items="nodeOptions"
            :search-input.sync="nodeSearch"
            label="nodes"
            multiple
            clearable
            outlined
            dense
            chips
            small-chips
            deletable-chips
            hide-details
            class="nodes"
            @change="pruneServices"
        >
            <template #selection="{ item, index }">
                <v-chip
                    v-if="index === 0 && !nodeSearch"
                    small
                    label
                    close
                    close-icon="mdi-close"
                    class="filter-selection"
                    @click:close="removeNode(item.value)"
                >
                    <span :title="item.text">{{ item.text }}</span>
                </v-chip>
                <span v-else-if="index === 1 && !nodeSearch" class="selection-count" :title="selectedNodeNames">+{{ selectedNodes.length - 1 }}</span>
            </template>
        </v-autocomplete>

        <v-autocomplete
            v-if="serviceGroups"
            v-model="selectedServices"
            :items="serviceOptions"
            :search-input.sync="serviceSearch"
            label="services"
            multiple
            clearable
            outlined
            dense
            chips
            small-chips
            hide-details
            class="services"
            no-data-text="No services on the selected nodes"
        >
            <template #selection="{ item, index }">
                <v-chip
                    v-if="index === 0 && !serviceSearch"
                    small
                    label
                    close
                    close-icon="mdi-close"
                    class="filter-selection"
                    @click:close="removeService(item.value)"
                >
                    <span :title="item.text">{{ item.text }}</span>
                </v-chip>
                <span v-else-if="index === 1 && !serviceSearch" class="selection-count" :title="selectedServiceNames"
                    >+{{ selectedServices.length - 1 }}</span
                >
            </template>
        </v-autocomplete>

        <div>
            <v-autocomplete
                :items="namespaces"
                v-model="selectedNamespaces"
                label="namespaces"
                :disabled="namespacesDisabled"
                color="primary"
                multiple
                outlined
                dense
                chips
                small-chips
                deletable-chips
                hide-details
                class="namespaces"
                :class="{ empty: !selectedNamespaces.length }"
            >
                <template #selection="{ item }">
                    <v-chip
                        small
                        label
                        :disabled="namespacesDisabled"
                        close
                        close-icon="mdi-close"
                        @click:close="removeNamespace(item.value)"
                        color="primary"
                        class="namespace"
                    >
                        <span :title="item.text">{{ item.name }}</span>
                    </v-chip>
                </template>
            </v-autocomplete>
        </div>

        <div class="d-none d-sm-block flex-grow-1" />

        <div class="d-flex flex-wrap align-center categories">
            <v-checkbox
                v-for="c in categories"
                :key="c"
                :value="c"
                v-model="selectedCategories"
                :label="c"
                :disabled="categoriesDisabled"
                class="category"
                color="primary"
                hide-details
            />

            <v-tooltip bottom>
                <template #activator="{ on }">
                    <v-btn :to="{ name: 'project_settings', params: { tab: 'applications' } }" v-on="on" icon x-small>
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </template>
                <v-card class="px-2">configure categories</v-card>
            </v-tooltip>
        </div>
    </div>
</template>

<script>
const storageKey = 'application-filter';

function autoSelectNamespace(namespaces, maxApps) {
    let ns = namespaces.find((ns) => ns.value === 'default');
    if (ns && ns.apps <= maxApps) {
        return ns.value;
    }
    let name = '';
    let apps = 0;
    namespaces.forEach((ns) => {
        if (ns.value && ns.apps > apps && ns.apps <= maxApps) {
            name = ns.value;
            apps = ns.apps;
        }
    });
    if (name) {
        return name;
    }
    return namespaces[0].value;
}

export default {
    props: {
        applications: Array,
        autoSelectNamespaceThreshold: Number,
        highlightSearch: Boolean,
        searchInfo: String,
        nodes: Array,
        serviceGroups: Array,
    },

    data() {
        return {
            selectedCategories: [],
            selectedNamespaces: [],
            selectedNodes: [],
            selectedServices: [],
            nodeSearch: '',
            serviceSearch: '',
            searchString: '',
            autoSelectNamespace: false,
            loaded: false,
        };
    },

    mounted() {
        this.load();
        this.$events.watch(this, this.load, 'refresh');
        if (this.$route.query.search) {
            this.searchString = this.$route.query.search;
        }
    },

    computed: {
        search() {
            return (this.searchString || '').trim();
        },
        nodeOptions() {
            const options = (this.nodes || []).map((node) => ({
                value: `${node.cluster_id}:${node.name}`,
                text: `${node.display_name || node.name}${this.$api.context.multicluster ? ` (${node.cluster_name})` : ''}`,
            }));
            // Retain selections if a node temporarily disappears during a refresh.
            this.selectedNodes.forEach((value) => {
                if (!options.some((node) => node.value === value)) options.push({ value, text: value });
            });
            return options.sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true, sensitivity: 'base' }));
        },
        selectedNodeNames() {
            return this.nodeOptions
                .filter((node) => this.selectedNodes.includes(node.value))
                .map((node) => node.text)
                .join(', ');
        },
        nodeApplications() {
            const selectedNodes = new Set(this.selectedNodes);
            return (this.applications || []).filter(
                (app) => !this.nodes || !selectedNodes.size || (app.nodes || []).some((node) => selectedNodes.has(node)),
            );
        },
        serviceOptions() {
            const groups = this.serviceGroups || [];
            const knownGroups = new Set(groups.map((group) => group.id));
            const available = new Set(this.nodeApplications.map((app) => (knownGroups.has(app.group) ? app.group : 'other')));
            const options = groups
                .filter((group) => !this.selectedNodes.length || available.has(group.id))
                .map((group) => ({ value: group.id, text: group.name }));
            options.sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true, sensitivity: 'base' }));
            if (available.has('other')) options.push({ value: 'other', text: 'Other applications' });
            return options;
        },
        selectedServiceNames() {
            return this.serviceOptions
                .filter((group) => this.selectedServices.includes(group.value))
                .map((group) => group.text)
                .join(', ');
        },
        categories() {
            const set = new Set(this.selectedCategories);
            (this.applications || []).forEach((a) => {
                set.add(a.category);
            });
            const categories = Array.from(set);
            categories.sort((a, b) => a.localeCompare(b));
            return categories;
        },
        searchFilters() {
            return !!this.search && !this.highlightSearch;
        },
        categoriesDisabled() {
            return this.searchFilters || !!this.selectedNamespaces.length;
        },
        namespaces() {
            const map = {};
            this.selectedNamespaces.forEach((ns) => {
                map[ns] = 0;
            });
            (this.applications || []).forEach((a) => {
                const id = this.$utils.appId(a.id);
                if (!map[id.ns]) {
                    map[id.ns] = 0;
                }
                map[id.ns]++;
            });
            const namespaces = Object.keys(map).map((ns) => {
                const name = ns || '~empty';
                const apps = map[ns];
                return { value: ns, name, apps, text: `${name} (${apps})` };
            });
            namespaces.sort((a, b) => a.value.localeCompare(b.value));
            return namespaces;
        },
        namespacesDisabled() {
            return this.searchFilters;
        },
        filter() {
            const selectedCategories = new Set(this.selectedCategories);
            const selectedNamespaces = new Set(this.selectedNamespaces);
            const selectedServices = new Set(this.selectedServices);
            const knownGroups = new Set((this.serviceGroups || []).map((group) => group.id));
            const search = this.search;
            const applications = this.nodeApplications.filter((a) => {
                if (this.serviceGroups && selectedServices.size && !selectedServices.has(knownGroups.has(a.group) ? a.group : 'other')) return false;
                if (this.searchFilters) {
                    return a.id.includes(search) || (a.type && a.type.name.includes(search)) || (a.cluster && a.cluster.includes(search));
                }
                if (selectedNamespaces.size) {
                    return selectedNamespaces.has(this.$utils.appId(a.id).ns);
                }
                return selectedCategories.has(a.category);
            });
            return new Set(applications.map((a) => a.id));
        },
    },

    watch: {
        filter: {
            handler() {
                if (!this.loaded) return;
                if (!this.selectedCategories.length && this.categories.length) {
                    this.selectedCategories.push(this.categories[0]);
                    this.save();
                    return;
                }
                if (
                    this.autoSelectNamespace &&
                    this.filter.size > this.autoSelectNamespaceThreshold &&
                    !this.selectedNamespaces.length &&
                    this.namespaces
                ) {
                    this.selectedNamespaces.push(autoSelectNamespace(this.namespaces, this.autoSelectNamespaceThreshold));
                    this.autoSelectNamespace = false;
                    this.save();
                    return;
                }
                this.$emit('filter', this.filter);
            },
            immediate: true,
        },
        search: {
            handler(v) {
                this.$emit('search', v);
            },
            immediate: true,
        },
        selectedCategories() {
            this.save();
        },
        selectedNamespaces() {
            this.save();
        },
        selectedNodes() {
            this.save();
        },
        selectedServices() {
            this.save();
        },
        '$route.params.projectId'() {
            this.load();
        },
    },

    methods: {
        searchInput() {
            return this.$refs.search.$refs.input;
        },
        clearSearch() {
            this.searchString = '';
        },
        removeNamespace(ns) {
            const i = this.selectedNamespaces.indexOf(ns);
            if (i >= 0) {
                this.selectedNamespaces.splice(i, 1);
            }
        },
        removeNode(node) {
            this.selectedNodes = this.selectedNodes.filter((value) => value !== node);
            this.pruneServices();
        },
        removeService(service) {
            this.selectedServices = this.selectedServices.filter((value) => value !== service);
        },
        pruneServices() {
            if (!this.serviceGroups) return;
            const available = new Set(this.serviceOptions.map((group) => group.value));
            this.selectedServices = this.selectedServices.filter((value) => available.has(value));
        },
        load() {
            this.loaded = false;
            this.nodeSearch = '';
            this.serviceSearch = '';
            const projectId = this.$route.params.projectId;
            let saved = this.$storage.local(storageKey) || {};
            saved = saved[projectId] || {};
            this.selectedCategories = saved.categories || [];
            this.selectedNamespaces = saved.namespaces || [];
            this.selectedNodes = saved.nodes || [];
            this.selectedServices = saved.services || [];
            this.autoSelectNamespace = !!this.autoSelectNamespaceThreshold && !this.selectedNamespaces.length;
            this.loaded = true;
        },
        save() {
            const saved = this.$storage.local(storageKey) || {};
            const projectId = this.$route.params.projectId;
            if (!saved[projectId]) {
                saved[projectId] = {};
            }
            saved[projectId].categories = this.selectedCategories;
            saved[projectId].namespaces = this.selectedNamespaces;
            saved[projectId].nodes = this.selectedNodes;
            saved[projectId].services = this.selectedServices;
            this.$storage.local(storageKey, saved);
        },
    },
};
</script>

<style scoped>
.categories {
}
.category {
    white-space: nowrap;
    margin: 0 12px 0 0;
    padding: 0;
}
.category:deep(.v-input--selection-controls__input) {
    margin-right: 2px !important;
}
*:deep(.v-list-item) {
    font-size: 14px !important;
    padding: 0 8px !important;
}
*:deep(.v-list-item__action) {
    margin: 4px !important;
}
.namespaces:deep(input) {
    width: 0 !important;
}
.namespaces.empty:deep(input) {
    width: 100px !important;
}
.namespace {
    margin: 4px 4px 0 0 !important;
    padding: 0 8px !important;
}
.namespace span {
    max-width: 20ch;
    overflow: hidden;
    text-overflow: ellipsis;
}
.namespace:deep(.v-icon) {
    font-size: 16px !important;
}
.search {
    width: 250px;
}
.nodes,
.services {
    min-width: 0;
    width: 200px;
    max-width: 200px;
    flex: 0 0 auto;
}
.nodes:deep(.v-select__selections),
.services:deep(.v-select__selections) {
    min-width: 0;
    flex-wrap: nowrap;
}
.nodes:deep(input),
.services:deep(input) {
    min-width: 0 !important;
    width: 0;
}
.filter-selection {
    min-width: 0;
    max-width: 100%;
    flex: 0 1 auto;
    margin: 2px 0 !important;
}
.filter-selection:deep(.v-chip__content) {
    min-width: 0;
    max-width: 100%;
}
.filter-selection span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.filter-selection:deep(.v-chip__close) {
    flex-shrink: 0;
}
.selection-count {
    flex-shrink: 0;
    margin-left: 4px;
    font-size: 12px;
}
</style>
